import { Check } from 'lucide-react';

import React from 'react';

import { cn } from '@/lib/utils';

export interface StepperStep {
  key: string;
  label: string;
}

interface Props {
  steps: StepperStep[];
  currentKey: string;
  /** Every step done, including the current one: the import has finished. */
  completed?: boolean;
  /** Read by assistive tech in place of the visual order. */
  ariaLabel: string;
}

/** Where the wizard is: ticked steps behind, the current one in orange. */
export function ImportStepper({
  steps,
  currentKey,
  completed = false,
  ariaLabel,
}: Props) {
  const at = completed
    ? steps.length
    : steps.findIndex(step => step.key === currentKey);
  return (
    <ol
      aria-label={ariaLabel}
      data-testid="import-stepper"
      className="flex items-start"
    >
      {steps.map((step, index) => {
        const state = index < at ? 'done' : index === at ? 'active' : 'pending';
        return (
          <li
            key={step.key}
            data-testid={`import-stepper-${step.key}`}
            data-state={state}
            aria-current={state === 'active' ? 'step' : undefined}
            className="flex min-w-0 flex-1 flex-col items-center gap-1.5"
          >
            <div className="flex w-full items-center">
              <div
                className={cn(
                  'h-0.5 flex-1',
                  index === 0
                    ? 'bg-transparent'
                    : state === 'pending'
                      ? 'bg-gray-200 dark:bg-gray-700'
                      : 'bg-primary-500'
                )}
              />
              <div
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  state === 'done' && 'bg-primary-500 text-white',
                  state === 'active' &&
                    'border-2 border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300',
                  state === 'pending' &&
                    'border-2 border-gray-300 text-gray-400 dark:border-gray-600 dark:text-gray-500'
                )}
              >
                {state === 'done' ? (
                  <Check className="h-4 w-4" strokeWidth={3} aria-hidden />
                ) : (
                  index + 1
                )}
              </div>
              <div
                className={cn(
                  'h-0.5 flex-1',
                  index === steps.length - 1
                    ? 'bg-transparent'
                    : state === 'done'
                      ? 'bg-primary-500'
                      : 'bg-gray-200 dark:bg-gray-700'
                )}
              />
            </div>
            <span
              className={cn(
                'truncate text-xs',
                state === 'active'
                  ? 'font-semibold text-gray-900 dark:text-white'
                  : state === 'done'
                    ? 'text-gray-600 dark:text-gray-300'
                    : 'text-gray-400 dark:text-gray-500'
              )}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
